import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { replace, useLoaderData, useNavigate } from 'react-router-dom'
import { useClerk, useUser } from '@clerk/clerk-react'
import './phone.css'

// COMPONENTS
import AnchorLink from '@components/atoms/anchor-link'
import Button from '@components/atoms/button'
import OneTimePassword from '@components/molecules/one-time-password'
import Spinner from '@components/atoms/spinner'
import TextField from '@components/atoms/textfield'
import { Column } from '@components/templates/column'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { useFixedSettings } from '@components/templates/fixed/_localStore/_hooks'
import { useInput } from '@hooks/inputs'
import { getErrorMessage } from '@utils/error-helper'
import { formatInput, normalizeInput } from '@utils/phone-number-helper'
import { createAuthenticatedLoader } from '@utils/loaders'
import { getPendingPhoneNumber, getRequiredKycPhonePath, hasRequiredName, normalizeSearch } from '@utils/user-onboarding'
import { isNonEmptyString } from '@utils/type-checkers'
import { parseTeraUserData } from '@schemas'


interface LoaderData {
    pendingPhoneId?: string
    pendingPhoneNumber?: string
}

const addFormId = 'kyc-phone-form'
const verifyFormId = 'kyc-phone-verification-form'
const disableDuration = 30
const placeholder = '+1 (555) 555-5555'
const maxPhoneLength = placeholder.length
const otpLength = 6

function isPhoneAlreadyUsedError(error: unknown): boolean {
    if (typeof error !== 'object' || error == null || !('errors' in error)) return false
    const errors = (error as { errors?: Array<{ code?: string }> }).errors
    return Array.isArray(errors) && errors[0]?.code === 'form_identifier_exists'
}

const Component: FC = () => {
    const { pendingPhoneId, pendingPhoneNumber } = useLoaderData() as LoaderData
    const { signOut } = useClerk()
    const { user } = useUser()
    const navigate = useNavigate()
    const { createToast } = useFixedSettings()
    const [phoneNumber, phoneNumberProps, setPhoneNumber] = useInput({
        initValue: pendingPhoneNumber ? formatInput(pendingPhoneNumber) : '',
        onChange: (value) => formatInput(value, phoneNumber)
    })
    const [otp, otpProps, setOtp] = useInput()
    const [currentPhoneId, setCurrentPhoneId] = useState(pendingPhoneId || '')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSendingCode, setIsSendingCode] = useState(false)
    const [isPhoneTaken, setIsPhoneTaken] = useState(false)
    const [phoneTakenNumber, setPhoneTakenNumber] = useState('')
    const [currentCountDown, setCurrentCountDown] = useState(disableDuration)
    const [otpSubmitEnabled, setOtpSubmitEnabled] = useState(false)
    const dialogRef = useRef<HTMLDialogElement>(null)
    const spinnerDialogRef = useRef<HTMLDialogElement>(null)
    const autoPreparedPhoneIdsRef = useRef<Set<string>>(new Set())

    const isVerifyStep = isNonEmptyString(currentPhoneId)
    const activeFormId = isVerifyStep ? verifyFormId : addFormId

    useEffect(() => {
        if (!dialogRef.current) return

        if (isPhoneTaken) dialogRef.current.showModal()
        else dialogRef.current.close()
    }, [isPhoneTaken])

    useEffect(() => {
        if (!spinnerDialogRef.current) return

        if (isSubmitting && !spinnerDialogRef.current.open) spinnerDialogRef.current.showModal()
        else if (!isSubmitting && spinnerDialogRef.current.open) spinnerDialogRef.current.close()
    }, [isSubmitting])

    const sendVerificationCode = useCallback(async (phoneId: string, showSuccessToast = false) => {
        try {
            const resource = user?.phoneNumbers.find(({ id }) => id === phoneId)
            if (!resource || resource.verification.status === 'verified') return

            setCurrentCountDown(disableDuration)
            setOtpSubmitEnabled(false)
            setIsSendingCode(true)
            await resource.prepareVerification()
            if (showSuccessToast) createToast({ type: 'success', text: 'Code sent.' })
        } catch (error: unknown) {
            console.error(error)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Failed to send verification code.')
            })
        } finally {
            setIsSendingCode(false)
        }
    }, [createToast, user])

    useEffect(() => {
        if (!currentPhoneId) return
        if (autoPreparedPhoneIdsRef.current.has(currentPhoneId)) return

        autoPreparedPhoneIdsRef.current.add(currentPhoneId)
        void sendVerificationCode(currentPhoneId)
    }, [currentPhoneId, sendVerificationCode])

    useEffect(() => {
        if (!isVerifyStep) return

        if (currentCountDown <= 0) {
            setOtpSubmitEnabled(true)
            return
        }

        const intervalId = window.setInterval(() => {
            setCurrentCountDown((prevCount) => prevCount - 1)
        }, 1000)

        return () => window.clearInterval(intervalId)
    }, [currentCountDown, isVerifyStep])

    const submittable = useMemo(() => {
        if (isVerifyStep) return otp.length === otpLength
        return normalizeInput(phoneNumber).length === 10
    }, [isVerifyStep, otp, phoneNumber])

    async function handleAddPhone(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()
            if (!user) throw new Error('Auth Services have yet to load.')

            setIsSubmitting(true)
            const normalizedPhoneNumber = `+1${normalizeInput(phoneNumber)}`
            const resource = await user.createPhoneNumber({ phoneNumber: normalizedPhoneNumber })
            setCurrentPhoneId(resource.id)
            setOtp('')
        } catch (error: unknown) {
            console.error(error)
            if (isPhoneAlreadyUsedError(error)) {
                setPhoneTakenNumber(formatInput(phoneNumber))
                setIsPhoneTaken(true)
                return
            }

            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to add your phone number.')
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    async function handleVerifyPhone(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()

            const resource = user?.phoneNumbers.find(({ id }) => id === currentPhoneId)
            if (!user || !resource) throw new Error('Phone number is no longer available.')
            if (!isNonEmptyString(otp)) throw new Error('Enter the 6-digit verification code.')

            setIsSubmitting(true)
            const result = await resource.attemptVerification({ code: otp })
            if (result.verification.status !== 'verified') throw new Error(`Verification failed with status: ${result.verification.status}`)

            if (user.primaryPhoneNumberId !== result.id) await user.update({ primaryPhoneNumberId: result.id })
            navigate(`/sign-up/kyc${document.location.search}`, { replace: true })
        } catch (error: unknown) {
            console.error(error)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to verify your phone number.')
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    async function handleUseDifferentNumber() {
        const resource = user?.phoneNumbers.find(({ id }) => id === currentPhoneId)
        const previousPhoneId = currentPhoneId
        const previousPhoneNumber = phoneNumber

        setCurrentPhoneId('')
        setOtp('')
        setPhoneNumber('')
        setCurrentCountDown(disableDuration)
        setOtpSubmitEnabled(false)

        try {
            if (!resource) return

            await resource.destroy()
            await user?.reload()
        } catch (error) {
            console.error(error)
            autoPreparedPhoneIdsRef.current.delete(previousPhoneId)
            setCurrentPhoneId(previousPhoneId)
            setPhoneNumber(previousPhoneNumber)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to reset your phone number.')
            })
        }
    }

    async function handleResendCode() {
        if (!currentPhoneId) return
        await sendVerificationCode(currentPhoneId, true)
    }

    async function handleSignInInstead() {
        try {
            await signOut()
            navigate('/sign-in', {
                replace: true,
                state: { initialView: 'form', intent: 'sign-in' }
            })
        } catch (error) {
            console.error(error)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to sign out and switch accounts.')
            })
        }
    }

    return (<>
        <Column.Content style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            {!isVerifyStep && (
                <div className='flex flex-col items-center gap-2 mt-8'>
                    <h1 className='text-center text-blue-500'>Add Your Phone Number</h1>
                    <p className='text-center'>We need your phone number before we can complete identity verification.</p>
                </div>
            )}
            {isVerifyStep && (
                <div className='flex flex-col items-center gap-2 mt-8'>
                    <h1 className='text-center text-blue-500'>Enter Your Verification Code</h1>
                </div>
            )}
            <div style={{ flex: 1 }} />

            {!isVerifyStep && (
                <form id={addFormId} onSubmit={handleAddPhone} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                    <TextField
                        {...phoneNumberProps}
                        type='tel'
                        autoComplete='tel'
                        autoCapitalize='off'
                        autoCorrect='off'
                        inputMode='tel'
                        name='phone-number'
                        maxLength={maxPhoneLength}
                        placeholder='Phone Number'
                        divideElementLeft={true}
                        inlineElementLeft={<p><strong>+1</strong></p>}
                        required
                    />
                </form>
            )}

            {isVerifyStep && (
                <>
                    <form id={verifyFormId} onSubmit={handleVerifyPhone} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
                        <OneTimePassword className='kyc-phone-otp' onChange={otpProps.onChange} value={otp} />
                    </form>
                    <p style={{ alignSelf: 'center', marginTop: '1rem' }}>
                        {isSendingCode
                            ? <span>Sending code...</span>
                            : !otpSubmitEnabled
                                ? <span>{`Resend in ${currentCountDown}s`}</span>
                                : <AnchorLink onClick={handleResendCode}>Resend Code</AnchorLink>
                        }
                    </p>
                    <small style={{ alignSelf: 'center', marginTop: '0.5rem' }}>
                        <button
                            type='button'
                            className='text-gray-500'
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'inherit' }}
                            onClick={handleUseDifferentNumber}
                            disabled={isSendingCode || isSubmitting}
                        >
                            Use a different number
                        </button>
                    </small>
                </>
            )}

            <div style={{ flex: 2 }} />
        </Column.Content>
        <Column.Footer>
            <Button form={activeFormId} type='submit' disabled={!submittable || isSubmitting || isSendingCode}>
                {isSubmitting ? (isVerifyStep ? 'Verifying...' : 'Sending Code...') : 'Continue'}
            </Button>
        </Column.Footer>
        <dialog ref={dialogRef}>
            <div>
                <div className='flex flex-col items-center justify-center text-center' style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1.5rem', borderRadius: 'var(--border-radius)', overflowY: 'auto', maxWidth: '22rem' }}>
                    <h3>This phone number is already in use</h3>
                    <p>
                        {phoneTakenNumber ? `${phoneTakenNumber} is already attached to an existing account.` : 'This phone number is already attached to an existing account.'}
                    </p>
                    <p>Please sign in with that phone number or enter a different one to continue.</p>
                    <div className='flex flex-col' style={{ gap: '0.75rem', width: '100%' }}>
                        <Button type='button' theme='primary' onClick={handleSignInInstead}>
                            Sign In Instead
                        </Button>
                        <Button type='button' theme='secondary' onClick={() => setIsPhoneTaken(false)}>
                            Use Different Number
                        </Button>
                    </div>
                </div>
            </div>
        </dialog>
        <dialog ref={spinnerDialogRef}>
            <div>
                <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                    <Spinner />
                </div>
            </div>
        </dialog>
    </>)
}

const loader = createAuthenticatedLoader(({ request }, Clerk) => {
    const search = normalizeSearch(new URL(request.url).search)
    const tera = parseTeraUserData(Clerk.user?.publicMetadata.tera)
    if (!hasRequiredName(Clerk.user)) return replace(`/sign-up/add-info${search}`)
    if (!tera.address) return replace(`/sign-up/billing${search}`)

    const phonePath = getRequiredKycPhonePath(Clerk.user, search)
    if (!phonePath) return replace(`/sign-up/kyc${search}`)
    if (phonePath !== `/sign-up/kyc/phone${search}`) return replace(`/sign-up/kyc/phone${search}`)

    const pendingPhone = getPendingPhoneNumber(Clerk.user)
    return {
        pendingPhoneId: pendingPhone?.id,
        pendingPhoneNumber: pendingPhone?.phoneNumber
    } satisfies LoaderData
})

const KycPhoneRoute: RouteObject = {
    path: 'phone',
    loader,
    Component,
}

export { KycPhoneRoute }
